/* eslint-disable graphql/template-strings */
import { useApolloClient, gql } from '@apollo/client';
import { useDates, useSelector } from '~Hooks';
import { DateTime } from 'luxon';
import { useStorage } from '~Components/Storage';

const CASH_EVENTS_QUERY = gql`
  query GetCashEvents($deviceId: ID!, $sentAfter: DateTime!) {
    POSDevice(id: $deviceId) {
      id
      cashEvents {
        events(sentAfter: $sentAfter) {
          lastKey
          items
        }
      }
    }
  }
`;

export interface CashRegisterTotals {
  cashInitial: number;
  cashSalesIncTips: number;
  cashDeposits: number;
  cashWithdrawals: number;
  cashFinal: number;
}

interface CashEvent {
  amount: number;
  clientId: string;
  companyId: string;
  createdAt: string;
  deviceId: string;
  eventId: string;
  sentAt: string;
  type: string;
  venueId: string;
  sourceId?: string;
}

const filterByDate = (events: CashEvent[], startDate: string, endDate: string) => {
  return events.filter((event) => {
    const eventDate = DateTime.fromISO(event.createdAt);
    return eventDate >= DateTime.fromISO(startDate) && eventDate <= DateTime.fromISO(endDate);
  });
};

/**
 * Processes cash events for a day for a specific device
 */
export const processCashEvents = (events: CashEvent[]): CashRegisterTotals => {
  const reversedEvents = events.slice().reverse();

  const startDayEvent = reversedEvents.find((event) => event.type === 'START_DAY');

  if (!startDayEvent) {
    return {
      cashInitial: 0,
      cashSalesIncTips: 0,
      cashDeposits: 0,
      cashWithdrawals: 0,
      cashFinal: 0,
    };
  }

  const eventsAfterStartDay = reversedEvents.filter(
    (event) => DateTime.fromISO(event.createdAt) >= DateTime.fromISO(startDayEvent.createdAt),
  );

  // If there is an END_DAY event, use its amount as cashFinal
  const lastEndDayEvent = eventsAfterStartDay
    .filter((event) => event.type === 'END_DAY')
    .at(-1);

  if (lastEndDayEvent) {
    return {
      cashInitial: startDayEvent.amount,
      cashSalesIncTips: 0,
      cashDeposits: 0,
      cashWithdrawals: 0,
      cashFinal: lastEndDayEvent.amount,
    };
  }

  // Initialize totals
  let cashSalesIncTips = 0;
  let cashDeposits = 0;
  let cashWithdrawals = 0;

  // Calculate totals
  const totalCashInRegister = eventsAfterStartDay.reduce((sum, event) => {
    // If there is a sourceId - then its a deposit or withdrawal
    if (event.sourceId) {
      if (event.amount > 0) {
        cashDeposits += event.amount;
      } else {
        cashWithdrawals += Math.abs(event.amount);
      }
    }

    if (!event.sourceId && event.type === 'CASH_CHANGE') {
      cashSalesIncTips += event.amount;
    }

    return sum + (event.amount || 0);
  }, 0);

  return {
    cashInitial: startDayEvent.amount,
    cashSalesIncTips,
    cashDeposits,
    cashWithdrawals,
    cashFinal: totalCashInRegister,
  };
};

export const useCashRegister = () => {
  const dates = useDates();
  const client = useApolloClient();
  const [storage] = useStorage();

  /**
   * Get the cash initial and cash final for a given dates latest START_DAY/END_DAY pair
   * If there is no matching END_DAY event, then the cash register was not closed and we throw an error
   * If there is no matching START_DAY event, then the cash register was not opened and we return 0
   */
  const getLatestDataForDate = async (date: string) => {
    if (!storage.taxComplianceDeviceId) {
      throw new Error('No deviceId found');
    }

    const d = new Date(date);

    const startDate = dates.getAccountingStartDate(d);
    const endDate = dates.getAccountingEndDate(d);

    // fecth all cash events after the startDate
    const response = await client.query({
      query: CASH_EVENTS_QUERY,
      variables: {
        deviceId: storage.taxComplianceDeviceId,
        sentAfter: startDate,
      },
      fetchPolicy: 'network-only',
    });

    const events = response?.data?.POSDevice?.cashEvents?.events?.items;

    // filter all cash events that are outside the range [startDate, endDate]
    const filteredEvents = filterByDate(events, startDate, endDate);

    return processCashEvents(filteredEvents);
  };

  return { getLatestDataForDate };
};
